// Единая логика «кому доступен раздел».
//
// Источник правды — mainMenu: если пункт виден только роли X, то и маршрут
// закрыт для остальных. Иначе меню и гард маршрутов разъезжаются: пункт скрыт,
// а страница открывается по прямой ссылке.
//
// Суперпользователь проходит любую проверку — так же, как require_roles
// на бэкенде.

import { matchPath } from 'react-router-dom';
import { mainMenu, type MenuDivider, type MenuEntry, type MenuItemBase } from '../constants/menu';
import { appSections, type AppSection } from '../constants/routes';
import type { RoleName } from '../types/auth';

const isDivider = (entry: MenuEntry): entry is MenuDivider =>
  'type' in entry && entry.type === 'divider';

/** Минимум, который нужен для проверки прав: подходит и User из контекста, и из shared/types. */
export interface RoleAwareUser {
  is_superuser?: boolean;
  roles?: { name: string }[];
}

/**
 * Проверка доступа по списку ролей.
 * `roles` не задан или пуст — раздел открыт любому авторизованному пользователю.
 */
export function canAccessRoles(
  user: RoleAwareUser | null | undefined,
  roles?: readonly string[],
): boolean {
  if (!roles || roles.length === 0) return true;
  if (!user) return false;
  if (user.is_superuser) return true;
  return (user.roles ?? []).some((role) => roles.includes(role.name));
}

/**
 * Карта «путь → роли, при которых пункт виден в меню».
 *
 * `undefined` вместо списка означает «открыто любому авторизованному»: так
 * помечены пункты без ролей и пути, встречающиеся в меню больше одного раза,
 * где хотя бы одно вхождение без ограничений. Для остальных повторов роли
 * объединяются — путь доступен всем, кто видит его хоть в одной группе.
 */
export function collectMenuRoles(
  menu: MenuEntry[] = mainMenu,
): Map<string, RoleName[] | undefined> {
  const result = new Map<string, RoleName[] | undefined>();

  const walk = (entries: MenuEntry[], inherited?: RoleName[]) => {
    for (const entry of entries) {
      if (isDivider(entry)) continue;

      const roles = entry.roles ?? inherited;

      if (entry.link) {
        if (result.has(entry.link)) {
          const known = result.get(entry.link);
          // одно из вхождений без ограничений — путь открыт
          result.set(
            entry.link,
            known && roles ? Array.from(new Set([...known, ...roles])) : undefined,
          );
        } else {
          result.set(entry.link, roles ? [...roles] : undefined);
        }
      }

      if (entry.children) walk(entry.children, roles);
    }
  };

  walk(menu);
  return result;
}

/**
 * Склеивает группы меню с одинаковым заголовком в одну.
 *
 * Группы заданы по ролям, поэтому у пользователя с несколькими ролями (и у
 * суперпользователя, который видит всё) в меню оказывалось три «Справочника»,
 * два «Основные» и два «Отчёта». Вызывать нужно ПОСЛЕ фильтрации по ролям:
 * склеиваются только те группы, которые пользователь и так видит.
 *
 * Повторяющиеся ссылки остаются один раз — по первому вхождению; блоки из
 * разных групп разделяются чертой.
 */
export function mergeMenuGroups(entries: MenuEntry[]): MenuEntry[] {
  const result: MenuEntry[] = [];
  const groupIndex = new Map<string, number>();

  for (const entry of entries) {
    if (isDivider(entry) || !entry.children) {
      result.push(entry);
      continue;
    }

    const known = groupIndex.get(entry.label);
    if (known === undefined) {
      groupIndex.set(entry.label, result.length);
      result.push({ ...entry, children: [...entry.children] });
      continue;
    }

    const target = result[known] as MenuItemBase;
    const links = new Set(
      target.children!.filter((c): c is MenuItemBase => !isDivider(c)).map((c) => c.link),
    );
    const added = entry.children.filter((c) => isDivider(c) || !links.has(c.link));
    if (added.some((c) => !isDivider(c))) {
      target.children = normalizeDividers([...target.children!, { type: 'divider' }, ...added]);
      target.roles =
        target.roles && entry.roles
          ? Array.from(new Set([...target.roles, ...entry.roles]))
          : undefined;
    }
  }

  return result;
}

/** Убирает разделители в начале и конце списка и склеивает идущие подряд. */
function normalizeDividers(entries: MenuEntry[]): MenuEntry[] {
  const out: MenuEntry[] = [];
  for (const entry of entries) {
    if (isDivider(entry) && (!out.length || isDivider(out[out.length - 1]))) continue;
    out.push(entry);
  }
  while (out.length && isDivider(out[out.length - 1])) out.pop();
  return out;
}

/** Все ссылки меню (без дублей) — например, чтобы найти активный пункт. */
export function collectMenuLinks(menu: MenuEntry[] = mainMenu): string[] {
  return Array.from(collectMenuRoles(menu).keys());
}

/**
 * Пункт меню, который надо подсветить для текущего адреса: из подходящих
 * берётся самый длинный. Сравнение по префиксу без этого зажигает сразу
 * несколько пунктов — на /documents/cash-withdrawals и «Поручения»
 * (/documents), и «Банковские операции».
 *
 * «Главная» (/ и /home) подсвечивается только при точном совпадении, иначе
 * она была бы активна на любой странице.
 */
export function findActiveMenuLink(
  pathname: string,
  links: string[] = collectMenuLinks(),
): string | null {
  if (pathname === '/' || pathname === '/home') {
    return links.find((link) => link === pathname) ?? null;
  }

  let best: string | null = null;
  for (const link of links) {
    if (link === '/' || link === '/home') continue;
    if (!matchPath({ path: link, end: false }, pathname)) continue;
    if (!best || link.length > best.length) best = link;
  }
  return best;
}

/**
 * Раздел, к которому относится адрес. Берётся самый длинный подошедший путь,
 * чтобы вложенные адреса (/dict/securities/5) достались своему разделу.
 * «Главная» пропускается: под неё подходит вообще всё.
 */
export function findSectionForPathname(
  pathname: string,
  sections: AppSection[] = appSections,
): AppSection | undefined {
  let best: AppSection | undefined;
  for (const section of sections) {
    if (section.path === '/' || section.path === '/home') continue;
    if (!matchPath({ path: section.path, end: false }, pathname)) continue;
    if (!best || section.path.length > best.path.length) best = section;
  }
  return best;
}

/**
 * Кому разрешена запись по этому адресу. `undefined` — раздел не объявлял
 * право записи, и страница решает сама (как было до введения writeRoles).
 */
export function writeRolesForPathname(
  pathname: string,
  sections: AppSection[] = appSections,
): RoleName[] | undefined {
  return findSectionForPathname(pathname, sections)?.writeRoles;
}

/**
 * Может ли пользователь писать по этому адресу.
 *
 * Раздел без writeRoles ограничений не накладывает — иначе введение поля
 * молча отключило бы кнопки на десятках экранов, которые о нём не знают.
 */
export function canWritePathname(
  user: RoleAwareUser | null | undefined,
  pathname: string,
  sections: AppSection[] = appSections,
): boolean {
  const roles = writeRolesForPathname(pathname, sections);
  if (!roles) return true;
  if (!user) return false;
  if (user.is_superuser) return true;
  return (user.roles ?? []).some((role) => roles.includes(role.name as RoleName));
}

/** Роли, нужные для пути. `undefined` — путь открыт любому авторизованному. */
export function rolesForPath(
  path: string,
  menu: MenuEntry[] = mainMenu,
): RoleName[] | undefined {
  return collectMenuRoles(menu).get(path);
}
